import { Alert, Button, Highlight, Loader, Paper, Select, Text, TextInput } from '@mantine/core';
import type { SelectItemProps, TextInputProps } from '@mantine/core';
import { forwardRef, useState } from 'react';
import type { useDentistPractices } from './useDentistPractices';

function searchTerms(query: string): string[] {
    return [...new Set(query.toLocaleLowerCase().split(/\s+/).filter(Boolean))]
        .sort((a, b) => b.length - a.length);
}

interface ContactOptionProps extends SelectItemProps {
    label: string;
    highlight: string[];
    selected?: boolean;
    disabled?: boolean;
    variant?: string;
}

const ContactOption = forwardRef<HTMLDivElement, ContactOptionProps>(function ContactOption(
    { label, value: _value, highlight, selected: _selected, disabled: _disabled, variant: _variant, ...props }, ref,
) {
    return <div ref={ref} {...props}>
        <Highlight component="span" inherit highlight={highlight} highlightColor="yellow.2"
            highlightStyles={theme => ({ color: theme.black })}
            sx={{ whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'anywhere' }}>
            {label}
        </Highlight>
    </div>;
});

interface ContactFieldsProps {
    directory: ReturnType<typeof useDentistPractices>;
    nameInputProps: TextInputProps;
    emailInputProps: TextInputProps;
    onSelect: (name: string, email: string) => void;
}

export function ContactFields({ directory, nameInputProps, emailInputProps, onSelect }: ContactFieldsProps) {
    const selected = directory.options.find(option => option.label === nameInputProps.value);
    const [manualEntry, setManualEntry] = useState(() => Boolean(nameInputProps.value && !selected));
    const [searchQuery, setSearchQuery] = useState('');
    // Mantine also emits the selected label through onSearchChange.
    const highlight = searchQuery === selected?.label ? [] : searchTerms(searchQuery);
    const nameInput = <TextInput
        withAsterisk label={directory.isAdmin ? 'Practice Name & Dentist Name' : 'Dentist Name & Practice Name'} placeholder="Your answer"
        {...nameInputProps} id="dentistNameAndPractiseName"
    />;
    const emailInput = <Paper shadow="xs" p="xl" mt="md" radius="md">
        <TextInput withAsterisk label="Email" description="Separate multiple email addresses with a space"
            placeholder="email1@example.com email2@example.com" {...emailInputProps} id="email" />
    </Paper>;

    if (!directory.isAdmin) return <>
        {emailInput}
        <Paper shadow="xs" p="xl" mt="md" radius="md">{nameInput}</Paper>
    </>;

    return <>
        <Paper shadow="xs" p="xl" mt="md" radius="md">
            {manualEntry ? nameInput : <>
                <Select
                    withAsterisk searchable clearable
                    clearButtonProps={{ 'aria-label': 'Clear practice and dentist' }}
                    label="Practice Name & Dentist Name"
                    placeholder={directory.isLoading ? 'Loading practices and dentists…' : 'Search practice or dentist'}
                    id="dentistNameAndPractiseName"
                    data={directory.options.map(option => ({ value: option.id, label: option.label, highlight }))}
                    itemComponent={ContactOption}
                    onSearchChange={setSearchQuery}
                    value={selected?.id ?? null}
                    onChange={id => {
                        setSearchQuery('');
                        const option = directory.options.find(item => item.id === id);
                        const addresses = [option?.dentistEmail ?? '', option?.practiceEmail ?? '']
                            .flatMap(value => value.split(/[\s,;]+/)).filter(Boolean);
                        const uniqueAddresses = new Map(addresses.map(email => [email.toLowerCase(), email]));
                        onSelect(option?.label ?? '', [...uniqueAddresses.values()].join(' '));
                    }}
                    error={nameInputProps.error}
                    disabled={directory.isLoading || !!directory.error}
                    rightSection={directory.isLoading ? <Loader size="xs" /> : undefined}
                    nothingFound="No matching dentist or practice. You can enter the details manually."
                    filter={(search, option) => {
                        const label = (option.label ?? '').toLocaleLowerCase();
                        return searchTerms(search).every(term => label.includes(term));
                    }}
                    limit={50}
                />
                {selected && !selected.dentistEmail && !selected.practiceEmail && <Text size="sm" color="dimmed" mt="xs">
                    This dentist and practice have no email on file. Enter an email address below.
                </Text>}
                {directory.error && <Alert color="red" mt="sm">
                    {directory.error}
                    <Button type="button" variant="subtle" size="xs" onClick={directory.retry}>Retry</Button>
                </Alert>}
            </>}
            <Button type="button" variant="subtle" size="xs" mt="xs" onClick={() => {
                if (manualEntry && !selected) onSelect('', '');
                setSearchQuery('');
                setManualEntry(value => !value);
            }}>
                {manualEntry ? 'Search existing contacts' : 'Enter manually'}
            </Button>
        </Paper>
        {emailInput}
    </>;
}
